import { Component, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { SeasonFarmsModel } from 'src/app/models/season-models/season-farms-model';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { SeasonSharedDataService } from 'src/app/services/season-shared-data.service';
import dayjs from "dayjs";
import { SeasonFieldsModel } from 'src/app/models/season-models/season-fields-model';
import { ExstraDetailModel } from 'src/app/models/season-models/exstra-detail-model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { DraftConfirmationDialogComponent } from 'src/app/dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component';
import { SeasonFieldBase } from 'src/app/dto/season-dto/season-field-base';
import { SeasonFieldInputComponent } from '../season-field-input/season-field-input.component';

@Component({
    selector: 'app-season-admin',
    templateUrl: './season-admin.component.html',
    styleUrls: ['./season-admin.component.css'],
    standalone: false
})
export class SeasonAdminComponent implements OnInit, OnDestroy {

  @ViewChildren(SeasonFieldInputComponent) fieldInputComponents: QueryList<SeasonFieldInputComponent>;

  seasonFieldDetailData: any = [];

  seasonFormData: any = null;
  seasonFormExtraData: {[key: number]: ExstraDetailModel} = {};

  currentField: any = null;
  totalFields: number = 0;

  isInAnotherActiveSeason: boolean = false;

  isCallDone: boolean = false;

  readonly dayjs = dayjs;

  placeholder: string = '-';
  dateStringFormatting = 'YYYY-MM-DD';

  private routeSub: Subscription;

  seasonId: number = 0;

  farms: SeasonFarmsModel[] = null;
  seasonFarmFields: SeasonFieldsModel[] = [];
  selectedFieldIndexByFarm: {[farmIndex: number]: number} = {};
  fieldTypes: any[] = [];

  // Multi-definition support
  fieldDefinitions: {[fieldId: number]: SeasonFieldBase[]} = {};
  selectedDefinitionIndexByField: {[fieldId: number]: number} = {};
  currentInputByField: {[fieldId: number]: any} = {};

  // Side panel state
  sidePanelOpen: boolean = false;
  selectedField: SeasonFieldsModel = null;
  selectedFarmIndex: number = 0;
  selectedFieldIndex: number = 0;
  panelTab: 'field' | 'crop' | 'cost' = 'field';


  constructor(
    public nav: NavBarComponent,
    private seasonManagerService: SeasonManagerService,
    private seasonSharedDataService: SeasonSharedDataService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
  ) {
  }
  ngOnDestroy(): void {
    // Clear the shared flag on the way out, so a field left invalid cannot
    // leave the SAVE button disabled on another tab.
    this.seasonSharedDataService.setCultivarSizeInvalid(false);
  }

  ngOnInit(): void {
    this.setSeasonId();
    this.populateSeasonFarmAndFieldDetails();
    this.seasonSharedDataService.setIsSaveBtnVisibleDataSubjectData(true);
    this.seasonSharedDataService.updateIsHiddenData(false);
    this.loadFieldTypes();
  }

  loadFieldTypes() {
    this.seasonSharedDataService.typeData$.subscribe(types => {
      this.fieldTypes = types || [];
    });
  }

  getFieldTypeName(fieldTypeId: number): string {
    const fieldType = this.fieldTypes.find(t => t.id === fieldTypeId);
    return fieldType ? fieldType.description : 'No Field type';
  }

  getFieldIndex(fieldIndex: number, farmIndex: number) {
    let currentField = this.farms[farmIndex].fields[fieldIndex];
    let index = this.seasonFieldDetailData.indexOf(currentField);
    if (index > -1) {
      return index;
    } else {
      return -1;
    }
  }

  /**
   * True while the open field's cultivar size would push it over its total.
   * Blocks anything that persists or navigates away: selecting another field,
   * definition tabs, panel tabs and Add Definition. Closing the panel stays
   * available but skips the save, so it is a way out without writing bad data.
   */
  cultivarSizeInvalid: boolean = false;

  /** Cultivar size rejection from the API, rendered on the input by the child. */
  cultivarSizeServerError: string = null;

  /**
   * Keeps the local flag and the shared one in step. The shared flag drives the
   * SAVE button, which lives in the parent season component.
   */
  private setCultivarSizeInvalid(invalid: boolean): void {
    this.cultivarSizeInvalid = invalid;
    this.seasonSharedDataService.setCultivarSizeInvalid(invalid);
  }

  /**
   * Pulls the message out of a 400 from the cultivar size check. Read
   * defensively: PG-358 throws a ResponseStatusException giving Spring's
   * default body, while the architecture document specifies a structured
   * shape. Anything else is ignored, so unrelated failures are not shown
   * against this field.
   */
  private extractCultivarSizeError(err: any): string | null {
    if (err?.status !== 400) return null;
    const body = err?.error;
    const message = typeof body === 'string' ? body : body?.message;
    if (typeof message === 'string' && message.toLowerCase().includes('cultivar size')) {
      return message;
    }
    return null;
  }

  onFormDataChange(index: number, farmIndex: number, data: any) {
    // Tracked outside the guard below so the blocks never go stale.
    this.setCultivarSizeInvalid(!!data?.name?.controls?.cultivarSize?.hasError('cultivarSizeSum'));

    if (data && !data.isFormPopulating) {
      this.currentField = this.farms[farmIndex].fields[index];
      const fieldId = this.currentField?.fieldId;

      // Update fieldDefinitions for the current definition
      if (fieldId && this.fieldDefinitions[fieldId]) {
        const defIndex = this.selectedDefinitionIndexByField[fieldId] ?? 0;
        const existing = this.fieldDefinitions[fieldId][defIndex] || {};
        const updatedFieldName = data.value.fieldName ?? existing.fieldName;
        const fieldTypeId = data.value.fieldTypeId ?? existing.fieldType;

        // Only store the extra-detail sub-object that matches this definition's fieldType.
        // Prevents cross-contamination when switching between definitions of the same type.
        const grainFeed = fieldTypeId == 1 ? data.value.grainFeed : (existing.grainFeed || null);
        const vegetables = fieldTypeId == 2 ? data.value.vegetables : (existing.vegetables || null);
        const vineyard = fieldTypeId == 3 ? data.value.vineyard : (existing.vineyard || null);
        const fruit = fieldTypeId == 4 ? data.value.fruit : (existing.fruit || null);

        this.fieldDefinitions[fieldId][defIndex] = {
          ...data.value,
          id: existing.id,
          fieldId: existing.fieldId ?? fieldId,
          fieldName: updatedFieldName,
          fieldType: fieldTypeId,
          currentCrop: data.value.currentCropId ?? existing.currentCrop,
          irrigationType: data.value.irrigationTypeId ?? existing.irrigationType,
          fieldSize: existing.fieldSize,
          isSelectedCropDefinition: existing.isSelectedCropDefinition,
          grainFeed,
          vegetables,
          vineyard,
          fruit,
        };

        // fieldName is a field-level property — propagate to all definitions so
        // whichever definition saves last still writes the correct name.
        if (updatedFieldName != null) {
          this.fieldDefinitions[fieldId].forEach((def: any, i: number) => {
            if (i !== defIndex) {
              def.fieldName = updatedFieldName;
            }
          });
          // Also update the field card and panel header so UI reflects the change immediately
          if (this.currentField) {
            this.currentField.fieldName = updatedFieldName;
          }
          if (this.selectedField?.fieldId === fieldId) {
            this.selectedField.fieldName = updatedFieldName;
          }
        }
      }

      // Rebuild seasonFieldDetailData with all definitions from all fields
      this.rebuildFormData();

      // Sync allowNotifications to the farm field model so farm-level toggle reflects changes
      if (this.currentField && data.value.allowNotifications !== undefined) {
        this.currentField.allowNotifications = data.value.allowNotifications;
      }

      this.updateBulkAllowNotifications(farmIndex);
    }

  }

  private rebuildFormData() {
    const allDefinitions: any[] = [];
    this.farms?.forEach(farm => {
      farm.fields?.forEach(field => {
        const defs = this.fieldDefinitions[field.fieldId];
        if (defs?.length > 0) {
          allDefinitions.push(...defs);
        } else {
          // Fallback to original field data if no definitions loaded
          const original = this.seasonFieldDetailData.find((d: any) => d.fieldId === field.fieldId || d.id === field.id);
          if (original) allDefinitions.push(original);
        }
      });
    });
    this.seasonFieldDetailData = allDefinitions;
    this.seasonSharedDataService.updateMultiFormData(this.seasonFieldDetailData);
  }

  populateSeasonFarmAndFieldDetails() {
    this.nav.toggleLoading(true);
    this.seasonFieldDetailData = [];
    this.seasonFarmFields = [];
    this.totalFields = 0;
    this.fieldDefinitions = {};
    this.currentInputByField = {};
    this.selectedDefinitionIndexByField = {};
    this.seasonFormExtraData = {};

    this.seasonManagerService.getSeasonById(this.seasonId).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.farms = response;

      this.farms.forEach(f => {
        this.totalFields += f.fields.length;
      });

      this.farms.forEach(farm => {
        this.seasonFarmFields.push(...farm.fields);

        if (farm?.fields?.length > 0) {
          farm.fields.forEach(field => {
            this.seasonFieldDetailData.push(field);
          })
        }
        this.setBulkAllowNotifications();
      });

      this.seasonSharedDataService.updateSeasonFieldData(this.seasonFarmFields);
      this.populateDetailField();

      // Load definitions for all fields
      this.farms.forEach(farm => {
        farm.fields?.forEach(field => {
          this.loadDefinitionsForField(field);
        });
      });
    });
  }

  populateDetailField() {
    if (this.seasonFieldDetailData.length === 0) {
      this.isCallDone = true;
      this.nav.toggleLoading(false);
      return;
    }
    for (let i = 0; i < this.seasonFieldDetailData.length; i++) {
      if (this.seasonFieldDetailData[i]?.fieldType) {
        this.populateExtraDetail(this.seasonFieldDetailData[i].id, this.seasonFieldDetailData[i].fieldType, i);
      } else {
        this.totalFields--;
      }

      if(!this.totalFields) {
        this.isCallDone = true;
        this.nav.toggleLoading(false);
      }
    }
  }

  populateExtraDetail(detailId: number, fieldTypeId: number, index: number) {
    const fieldId = this.seasonFieldDetailData[index]?.fieldId;
    this.seasonManagerService.getSeasonDetailExtraData(detailId, Number(fieldTypeId)).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.seasonFormExtraData[detailId] = response;
      if (fieldId) this.seasonFormExtraData[fieldId] = response;
      this.totalFields--;

      if(!this.totalFields) {
        this.isCallDone = true;
        this.seasonSharedDataService.updateMultiFormData(this.seasonFieldDetailData);
        this.nav.toggleLoading(false);
      }
    });
  }

  setSeasonId() {
    this.seasonSharedDataService.seasonIdData$.subscribe(data => {
      this.seasonId = data;
    });
  }

  onToggleBulkAllowNotifications(event: MatSlideToggleChange, farmIndex: number) {
    const checked = event.checked;
    this.farms[farmIndex].isBulkAllowNotification = checked;
    this.seasonSharedDataService.updateBulkAllowNotification(checked, farmIndex);

    // Persist to API for each field in this farm
    this.farms[farmIndex].fields.forEach(field => {
      field.allowNotifications = checked;
      this.seasonManagerService.updateFieldNameAllowNotification(field.fieldId, field.fieldName, checked).subscribe();
      // Also update in-memory definitions
      if (this.fieldDefinitions[field.fieldId]) {
        this.fieldDefinitions[field.fieldId].forEach(def => def.allowNotifications = checked);
      }
    });
  }


  setBulkAllowNotifications() {

    this.farms.forEach(farm => {
      farm.isBulkAllowNotification = !farm.fields.some(field => !field.allowNotifications);

    });
  }

  updateBulkAllowNotifications(farmIndex: number) {

    for (let field of this.farms[farmIndex].fields) {

      let updatedField = this.seasonFieldDetailData.find(f => f.fieldId === field.fieldId);

      if (!updatedField?.allowNotifications) {
        this.farms[farmIndex].isBulkAllowNotification = false;
        break;
      }
      this.farms[farmIndex].isBulkAllowNotification = true;
    }
  }

  selectField(farmIndex: number, fieldIndex: number) {
    this.selectedFieldIndexByFarm[farmIndex] = fieldIndex;
    const field = this.farms[farmIndex].fields[fieldIndex];
    if (field) this.loadDefinitionsForField(field);
  }

  openFieldPanel(farm: any, farmIndex: number, field: SeasonFieldsModel, fieldIndex: number) {
    // Opening any field saves the current one first, so refuse while the cultivar
    // size is invalid.
    //
    // This used to exempt the field already open, on the reasoning that
    // re-selecting it was harmless. It is not: the save below runs on the way to
    // switchToField whichever field was clicked, so clicking the open one was a
    // route to pushing the rejected value at the server. switchToField then made
    // it worse by reloading from the API, discarding the edits and clearing the
    // error the failed save had just set, so nothing on screen explained it.
    //
    // Matches closeSidePanel, which already declines to save when invalid.
    if (this.cultivarSizeInvalid && this.selectedField) {
      return;
    }

    // If a field is currently open, save it first then switch
    if (this.selectedField) {
      const previousFieldId = this.selectedField.fieldId;
      this.saveCurrentFieldData(previousFieldId, () => {
        this.switchToField(farm, farmIndex, field, fieldIndex);
      });
    } else {
      this.switchToField(farm, farmIndex, field, fieldIndex);
    }
  }

  private switchToField(farm: any, farmIndex: number, field: SeasonFieldsModel, fieldIndex: number) {
    // Drop any rejection from the field being left, so it does not appear
    // against the next field's input.
    this.cultivarSizeServerError = null;
    this.selectedField = field;
    this.selectedFarmIndex = farmIndex;
    this.selectedFieldIndex = fieldIndex;
    this.panelTab = 'field';
    this.sidePanelOpen = true;
    // Force fresh load from API (clear cache so it re-fetches)
    delete this.fieldDefinitions[field.fieldId];
    this.currentInputByField[field.fieldId] = null;
    this.loadDefinitionsForField(field);
  }

  closeSidePanel() {
    // Closing stays available as a way out, but skips the save when the
    // cultivar size is invalid - otherwise closing would persist the very
    // value every other control is blocking. Discarding matches how the
    // Field Details dialog behaves on close.
    if (this.selectedField && !this.cultivarSizeInvalid) {
      this.saveCurrentFieldData(this.selectedField.fieldId);
    }
    this.setCultivarSizeInvalid(false);
    this.cultivarSizeServerError = null;
    this.sidePanelOpen = false;
    this.selectedField = null;
  }

  /**
   * Save the currently open field's data using the same pattern as the SAVE button.
   * Saves base details + extra details for the active definition, then flushes costs.
   * After save, clears the cached definitions so next load is fresh from API.
   */
  private saveCurrentFieldData(fieldId: number, onComplete?: () => void): void {
    if (!fieldId) { if (onComplete) onComplete(); return; }

    // Flush any pending cost saves via the child component
    const inputComponent = this.fieldInputComponents?.first;
    if (inputComponent) {
      inputComponent.saveCosts();
    }

    // Save all definitions for this field (same pattern as saveFieldDefinitions)
    const definitions = this.fieldDefinitions[fieldId] || [];
    const activeIndex = this.selectedDefinitionIndexByField[fieldId] ?? 0;
    let remaining = definitions.length;
    if (remaining === 0) { if (onComplete) onComplete(); return; }

    definitions.forEach((def: any, i: number) => {
      if (!def?.id) { remaining--; if (remaining === 0 && onComplete) onComplete(); return; }

      const fieldTypeId = def?.fieldTypeId ?? def?.fieldType;
      const seasonDetails = {
        ...def,
        isSelectedCropDefinition: i === activeIndex,
        fieldTypeId: fieldTypeId,
        currentCropId: def?.currentCropId ?? def?.currentCrop,
        irrigationTypeId: def?.irrigationTypeId ?? def?.irrigationType,
      };
      ['fieldId', 'id', 'grainFeed', 'vegetables', 'vineyard', 'fruit'].forEach(key => delete seasonDetails[key]);

      this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe(() => {
        this.cultivarSizeServerError = null;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      }, (err: any) => {
        const message = this.extractCultivarSizeError(err);
        if (message) this.cultivarSizeServerError = message;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      });

      // Save extra details (crop detail)
      if (fieldTypeId == 1 && def.grainFeed) {
        this.seasonManagerService.postSeasonDetailsGrainFeed(def.id, def.grainFeed).subscribe();
      }
      if (fieldTypeId == 2 && def.vegetables) {
        this.seasonManagerService.postSeasonDetailsVegetables(def.id, def.vegetables).subscribe();
      }
      if (fieldTypeId == 3 && def.vineyard) {
        this.seasonManagerService.postSeasonDetailsVineyard(def.id, def.vineyard).subscribe();
      }
      if (fieldTypeId == 4 && def.fruit) {
        this.seasonManagerService.postSeasonDetailsFruit(def.id, def.fruit).subscribe();
      }
    });

    // Clear cached definitions so next load fetches fresh from API
    // Keep currentInputByField so cards continue displaying the saved values
    delete this.fieldDefinitions[fieldId];
  }

  getSelectedFieldIndex(farmIndex: number): number {
    return this.selectedFieldIndexByFarm[farmIndex] ?? 0;
  }

  trackByFarm(index: number, farm: SeasonFarmsModel): any {
    return farm.id || index;
  }

  trackByField(index: number, field: SeasonFieldsModel): any {
    return field.id || index;
  }

  // Multi-definition methods
  loadDefinitionsForField(field: SeasonFieldsModel) {
    const fieldId = field.fieldId;
    if (this.fieldDefinitions[fieldId]) return;
    this.seasonManagerService.getDetailsOfFieldById(fieldId).subscribe((response: any) => {
      if (response?.details?.length > 0) {
        this.fieldDefinitions[fieldId] = response.details;
      } else if (response?.detail) {
        this.fieldDefinitions[fieldId] = [response.detail];
      }
      const defs = this.fieldDefinitions[fieldId] || [];
      const activeIdx = defs.findIndex((d: any) => d.isSelectedCropDefinition === true || d.isActive === true);
      const previousIdx = this.selectedDefinitionIndexByField[fieldId];
      const idx = activeIdx > -1 ? activeIdx : (previousIdx != null && previousIdx < defs.length ? previousIdx : 0);
      this.selectedDefinitionIndexByField[fieldId] = idx;
      this.currentInputByField[fieldId] = defs[idx] || field;

      // Fetch extra detail data for the active definition
      const activeDef = defs[idx];
      const typeId = Number(activeDef?.fieldType || activeDef?.fieldTypeId);
      if (activeDef?.id && typeId && !isNaN(typeId)) {
        this.seasonManagerService.getSeasonDetailExtraData(activeDef.id, typeId).subscribe((extraResponse) => {
          this.seasonFormExtraData[fieldId] = extraResponse;
        });
      }
    });
  }

  getDefinitions(field: SeasonFieldsModel): SeasonFieldBase[] {
    return this.fieldDefinitions[field.fieldId] || [];
  }

  getSelectedDefinitionIndex(field: SeasonFieldsModel): number {
    return this.selectedDefinitionIndexByField[field.fieldId] ?? 0;
  }

  onDefinitionTabChange(field: SeasonFieldsModel, index: number) {
    const fieldId = field.fieldId;

    // Backstop for the disabled tabs: the silent auto-save below must never
    // persist an invalid cultivar size.
    if (this.cultivarSizeInvalid && index !== this.getSelectedDefinitionIndex(field)) {
      return;
    }

    // Auto-save previous definition before switching
    this.saveCurrentDefinitionSilently(fieldId);
    this.selectedDefinitionIndexByField[fieldId] = index;
    // Update isSelectedCropDefinition flags in-memory
    const defs = this.fieldDefinitions[fieldId] || [];
    defs.forEach((d: any, i: number) => d.isSelectedCropDefinition = (i === index));
    const def = defs[index];

    // Clear stale extra detail data BEFORE updating inputData to prevent the child
    // component from reading the previous definition's crop-specific values during
    // the sync phase (before the async API fetch completes).
    this.seasonFormExtraData[fieldId] = null;

    this.currentInputByField[fieldId] = { ...def };
    // Mark new definition as selected and persist
    if (def?.id) {
      this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, { isSelectedCropDefinition: true }).subscribe();
    }
    const typeId = Number(def?.fieldType || def?.fieldTypeId);
    if (def?.id && typeId && !isNaN(typeId)) {
      this.seasonManagerService.getSeasonDetailExtraData(def.id, typeId).subscribe((response) => {
        this.seasonFormExtraData[fieldId] = response;
      });
    } else {
      this.seasonFormExtraData[fieldId] = null;
    }
  }

  private saveCurrentDefinitionSilently(fieldId: number) {
    const currentIndex = this.selectedDefinitionIndexByField[fieldId] ?? 0;
    const def: any = this.fieldDefinitions[fieldId]?.[currentIndex];
    if (!def?.id) return;

    const fieldTypeId = def?.fieldTypeId ?? def?.fieldType;
    const seasonDetails = {
      ...def,
      isSelectedCropDefinition: false,
      fieldTypeId: fieldTypeId,
      currentCropId: def?.currentCropId ?? def?.currentCrop,
      irrigationTypeId: def?.irrigationTypeId ?? def?.irrigationType,
    };
    ['fieldId', 'id', 'grainFeed', 'vegetables', 'vineyard', 'fruit'].forEach(key => delete seasonDetails[key]);

    this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe(
      () => this.cultivarSizeServerError = null,
      (err: any) => {
        const message = this.extractCultivarSizeError(err);
        if (message) this.cultivarSizeServerError = message;
      });

    if (fieldTypeId == 1 && def.grainFeed) {
      this.seasonManagerService.postSeasonDetailsGrainFeed(def.id, def.grainFeed).subscribe();
    }
    if (fieldTypeId == 2 && def.vegetables) {
      this.seasonManagerService.postSeasonDetailsVegetables(def.id, def.vegetables).subscribe();
    }
    if (fieldTypeId == 3 && def.vineyard) {
      this.seasonManagerService.postSeasonDetailsVineyard(def.id, def.vineyard).subscribe();
    }
    if (fieldTypeId == 4 && def.fruit) {
      this.seasonManagerService.postSeasonDetailsFruit(def.id, def.fruit).subscribe();
    }
  }

  getDefinitionLabel(def: SeasonFieldBase, index: number): string {
    return def?.cultivar || `Definition ${index + 1}`;
  }

  addDefinition(field: SeasonFieldsModel) {
    const dialogRef = this.matDialog.open(DraftConfirmationDialogComponent, {
      data: { dialogType: 'save', dialogTitle: 'Save Changes', dialogSubTitle: 'All current changes will be saved before creating a new definition.' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.saveFieldDefinitions(field.fieldId, () => {
        const fieldId = field.fieldId;
        const activeIndex = this.selectedDefinitionIndexByField[fieldId] ?? 0;
        const currentDef = this.fieldDefinitions[fieldId]?.[activeIndex];
        this.seasonManagerService.addDefinition(this.seasonId, field.fieldId, currentDef).subscribe((newDef) => {
      const fieldId = field.fieldId;
      if (!this.fieldDefinitions[fieldId]) this.fieldDefinitions[fieldId] = [];
      const flatDef: any = {
        id: newDef.id,
        fieldId: newDef.field?.id,
        fieldType: newDef.fieldType?.id,
        fieldTypeId: newDef.fieldType?.id,
        plantDate: newDef.plantDate,
        currentCrop: newDef.currentCrop?.id,
        cultivar: newDef.cultivar,
        yieldAverage: newDef.yieldAverage,
        yieldGoal: newDef.yieldGoal,
        expectedWaterAmount: newDef.expectedWaterAmount,
        irrigationType: newDef.irrigationType?.id,
        isIrrigation: newDef.isIrrigation,
        isHarvested: newDef.isHarvested,
        allowNotifications: newDef.field?.receiveNotifications,
        fieldName: newDef.field?.name,
        fieldSize: newDef.field?.size,
      };
      this.fieldDefinitions[fieldId] = [...this.fieldDefinitions[fieldId], flatDef];
      this.selectedDefinitionIndexByField[fieldId] = this.fieldDefinitions[fieldId].length - 1;
      this.currentInputByField[fieldId] = { ...flatDef };
      this.onDefinitionTabChange(field, this.selectedDefinitionIndexByField[fieldId]);
      // Force child component to reload all data (bypasses ngOnChanges guard)
      setTimeout(() => {
        const inputComponent = this.fieldInputComponents?.first;
        if (inputComponent) {
          inputComponent.reloadAllData();
        }
      });
      this.snackBar.open('Definition added', 'close', { duration: 3000 });
    });
      });
    });
  }

  private saveFieldDefinitions(fieldId: number, onComplete?: () => void) {
    const definitions = this.fieldDefinitions[fieldId] || [];
    const activeIndex = this.selectedDefinitionIndexByField[fieldId] ?? 0;
    let remaining = definitions.length;
    if (remaining === 0) { if (onComplete) onComplete(); return; }

    definitions.forEach((def: any, i: number) => {
      if (!def?.id) { remaining--; if (remaining === 0 && onComplete) onComplete(); return; }
      const seasonDetails = {
        ...def,
        isSelectedCropDefinition: i === activeIndex,
        fieldTypeId: def?.fieldTypeId ?? def?.fieldType,
        currentCropId: def?.currentCropId ?? def?.currentCrop,
        irrigationTypeId: def?.irrigationTypeId ?? def?.irrigationType,
      };
      ['fieldId', 'id', 'grainFeed', 'vegetables', 'vineyard', 'fruit'].forEach(key => delete seasonDetails[key]);

      this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe(() => {
        this.cultivarSizeServerError = null;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      }, (err: any) => {
        const message = this.extractCultivarSizeError(err);
        if (message) this.cultivarSizeServerError = message;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      });
    });
  }

  deleteDefinition(field: SeasonFieldsModel, def: SeasonFieldBase, event: Event) {
    event.stopPropagation();
    const fieldId = field.fieldId;
    if (this.fieldDefinitions[fieldId]?.length <= 1) return;

    const dialogRef = this.matDialog.open(DraftConfirmationDialogComponent, {
      data: { dialogType: 'delete', dialogTitle: 'Delete Definition', dialogSubTitle: 'Are you sure you want to delete this crop definition?' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.seasonManagerService.deleteDefinition(def.id).subscribe(() => {
        this.fieldDefinitions[fieldId] = this.fieldDefinitions[fieldId].filter(d => d.id !== def.id);
        if (this.selectedDefinitionIndexByField[fieldId] >= this.fieldDefinitions[fieldId].length) {
          this.selectedDefinitionIndexByField[fieldId] = this.fieldDefinitions[fieldId].length - 1;
        }
        this.onDefinitionTabChange(field, this.selectedDefinitionIndexByField[fieldId]);
        this.snackBar.open('Definition deleted', 'close', { duration: 3000 });
      });
    });
  }
}





